import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router';
import { useIsAuthenticated } from '@azure/msal-react';
import { useAuthFetch } from '../hooks/useAuthFetch';
import './FeedbackButton.css';

export default function FeedbackButton() {
  const isAuthenticated = useIsAuthenticated();
  const { authFetch } = useAuthFetch();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [type, setType] = useState('suggestion');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('smartstudy:open-feedback', handler);
    return () => window.removeEventListener('smartstudy:open-feedback', handler);
  }, []);

  if (!isAuthenticated) return null;

  const reset = () => {
    setType('suggestion');
    setMessage('');
    setSuccess(false);
    setSubmitting(false);
    setError(null);
  };

  const handleClose = () => {
    setOpen(false);
    setTimeout(reset, 200);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, message, page_url: location.pathname }),
      });
      // authFetch returns the RAW Response, and fetch only rejects on a network
      // error, so a 400/429/500 lands here RESOLVED. Without this check the modal
      // thanked the user for feedback the server had just thrown away.
      // The server's error key is `error` (its SUCCESS body uses `message`).
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.error || 'Failed to submit feedback. Please try again.');
        return; // modal stays open, the user's text is preserved, they can retry
      }
      setSuccess(true);
      setTimeout(handleClose, 2200);
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button className="feedback-fab" onClick={() => setOpen(true)} aria-label="Send feedback">
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        Feedback
      </button>

      {open && (
        <div className="feedback-backdrop" onClick={handleClose}>
          <div className="feedback-modal" onClick={e => e.stopPropagation()}>

            <div className="feedback-modal-header">
              <p className="feedback-modal-title">Send feedback</p>
              <button className="feedback-close-btn" onClick={handleClose}>✕</button>
            </div>

            {success ? (
              <div className="feedback-success">
                <span className="feedback-success-icon">✓</span>
                <p className="feedback-success-title">Thanks for the feedback!</p>
                <p className="feedback-success-sub">It helps make the app better.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="feedback-field">
                  <label className="feedback-label" htmlFor="fb-type">Type</label>
                  <select
                    id="fb-type"
                    className="feedback-select"
                    value={type}
                    onChange={e => setType(e.target.value)}
                  >
                    <option value="suggestion">Suggestion</option>
                    <option value="bug">Bug report</option>
                    <option value="general">General</option>
                  </select>
                </div>

                <div className="feedback-field">
                  <label className="feedback-label" htmlFor="fb-message">Message</label>
                  <textarea
                    id="fb-message"
                    className="feedback-textarea"
                    value={message}
                    onChange={e => { setMessage(e.target.value); if (error) setError(null); }}
                    placeholder="What's on your mind?"
                    required
                    maxLength={5000}
                    data-form-type="other"
                  />
                </div>

                {error && <p className="feedback-error" role="alert">{error}</p>}

                <div className="feedback-actions">
                  <button className="feedback-cancel-btn" type="button" onClick={handleClose}>Cancel</button>
                  <button className="feedback-submit-btn" type="submit" disabled={submitting || !message.trim()}>
                    {submitting ? 'Sending…' : 'Send feedback'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
